import { useState, useCallback, useRef, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useDebounce } from "../../hooks/useDebounce";

/**
 * Manages paginated + searchable GitHub repo fetching for a workflow.
 * Search is debounced and hits the API directly (server-side).
 * Exposes `loadMore` for infinite scroll and `initialize` to trigger the first fetch.
 */
export function useGitHubRepos(workflowId) {
    const [repos, setRepos] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [search, setSearch] = useState("");

    const initialized = useRef(false);
    const debouncedSearch = useDebounce(search, 400);
    const prevSearch = useRef(null);

    const { call: fetchPage, isLoading, error } = useBluelagoon({
        method: "getGitHubRepos",
        setIsLoading: true,
        resetResponse: false,
    });

    const load = useCallback(async (pg, q) => {
        const res = await fetchPage({ workflowId, page: pg, search: q }).catch(() => null);
        const data = res?.data;
        if (!data) return;
        setRepos((prev) => (pg === 1 ? data.repos : [...prev, ...data.repos]));
        setHasMore(data.hasMore);
    }, [fetchPage, workflowId]);

    // React to debounced search changes after initialization
    useEffect(() => {
        if (!initialized.current) return;
        if (debouncedSearch === prevSearch.current) return;
        prevSearch.current = debouncedSearch;
        setPage(1);
        load(1, debouncedSearch);
    }, [debouncedSearch, load]);

    // Called once when the dropdown opens for the first time
    const initialize = useCallback(() => {
        if (initialized.current) return;
        initialized.current = true;
        prevSearch.current = "";
        load(1, "");
    }, [load]);

    const loadMore = useCallback(() => {
        if (!hasMore || isLoading) return;
        const next = page + 1;
        setPage(next);
        load(next, debouncedSearch);
    }, [hasMore, isLoading, page, debouncedSearch, load]);

    return { repos, search, setSearch, isLoading, error, hasMore, loadMore, initialize };
}
